import { useCallback, useEffect, useState } from 'react';
import {
  api,
  type AdminCallDetail,
  type AdminCallRow,
  type AdminCallSummary,
  type CallCodec,
  type CallDegradation,
  type CallSettings,
} from '../api/client.ts';
import { formatDuration, formatLastSeen } from '../lib/format.ts';
import { CALL_METRIC_LABEL, type CallMetric } from '../lib/callChart.ts';
import { AdminCallChart } from './AdminCallChart.tsx';
import './AdminCallChart.css';

/**
 * The «Звонки» tab: what a call is allowed to cost, and the one question about
 * calls the server cannot answer for itself.
 *
 * Its own file and its own fetch, like the gallery tab: the admin screen polls
 * every three seconds, and none of this changes on a timer — a knob changes when
 * somebody turns it, and the relay is a button because whether it is reachable
 * is a fact about the network the phone is standing on rather than about this
 * server.
 *
 * The knobs used to be constants in the client's source, which made every
 * question about call quality — is 500 kbps too little, does maintain-framerate
 * suit a talking head, is H264 cheaper on this phone — cost a redeploy and a
 * pair of devices. They now travel with the ICE credentials every call fetches,
 * so a change here applies to the next call anywhere, with nobody reloading.
 */

/** Everything is typed into text inputs and parsed on save, as elsewhere here. */
interface Draft {
  callWidth: string;
  callHeight: string;
  callFps: string;
  callVideoKbps: string;
  callAudioKbps: string;
  callDegradation: CallDegradation;
  callCodec: CallCodec;
  callAudioFec: boolean;
  callRelayFirst: boolean;
  callStatsSeconds: string;
  callSamplesDays: string;
}

function draftFrom(settings: CallSettings): Draft {
  return {
    callWidth: String(settings.callWidth),
    callHeight: String(settings.callHeight),
    callFps: String(settings.callFps),
    callVideoKbps: String(settings.callVideoKbps),
    callAudioKbps: String(settings.callAudioKbps),
    callDegradation: settings.callDegradation,
    callCodec: settings.callCodec,
    callAudioFec: settings.callAudioFec,
    callRelayFirst: settings.callRelayFirst,
    callStatsSeconds: String(settings.callStatsSeconds),
    callSamplesDays: String(settings.callSamplesDays),
  };
}

const DEGRADATIONS: { value: CallDegradation; label: string }[] = [
  { value: 'balanced', label: 'Как решит браузер' },
  { value: 'maintain-framerate', label: 'Беречь плавность' },
  { value: 'maintain-resolution', label: 'Беречь чёткость' },
];

const CODECS: CallCodec[] = ['auto', 'H264', 'VP8', 'VP9', 'AV1'];

/**
 * The list of calls, and one of them opened.
 *
 * What is shown here was chosen the way the gallery tab's numbers were: every
 * column is one somebody would act on. The row answers «стоит ли открывать» —
 * when, who, how it ended, and the three figures that separate a good call from
 * a bad one — and the detail answers «что именно было плохо», which is the two
 * sides' charts and the log beside them.
 *
 * Loaded when the tab opens and on «Обновить», never on a timer: this is a
 * table that grows with every call, and the admin screen's three-second poll
 * has no business touching it.
 */

/** `500 кбит/с` / `1.5 Мбит/с` — the unit a bitrate is talked about in. */
function bitrate(bps: number | null): string {
  if (bps === null) return '—';
  return bps >= 1_000_000
    ? `${Math.round(bps / 100_000) / 10} Мбит/с`
    : `${Math.round(bps / 1000)} кбит/с`;
}

function percent(share: number | null): string {
  return share === null ? '—' : `${Math.round(share * 1000) / 10}%`;
}

/** «Исходящий · видео», and what became of it. */
function outcome(call: AdminCallRow): string {
  if (call.result === 'ended') return `разговор ${formatDuration(call.durationMs ?? 0)}`;
  if (call.result === 'missed') return call.endReason === 'timeout' ? 'не ответили' : 'отменён';
  if (call.result === 'declined') return 'отклонён';
  if (call.result === 'failed') return 'не соединился';
  return call.endedAt === null ? 'идёт' : (call.endReason ?? 'завершён');
}

/** 'page' | 'native' → what to call it on a page in Russian. */
/**
 * Which app a side was in, and it used to be read off `via` — which is not that
 * question. `via` says whether the media went through the page's peer or the
 * iOS shell's libwebrtc, and the Android app is on the page's peer by design,
 * so «браузер» was printed for every call ever made from the Android phone.
 * That is why there are two functions here now and why this one is the one on
 * the row: the peer only matters next to numbers, and it is in the detail.
 */
function peer(value: string | null): string {
  if (value === 'native') return 'нативный peer';
  if (value === 'page') return 'web-peer';
  return 'молчал';
}

function shell(value: string | null): string {
  if (value === 'android') return 'Android';
  if (value === 'ios') return 'iPhone';
  if (value === 'web') return 'браузер';
  return 'молчал';
}

const METRICS: CallMetric[] = ['bitrate', 'voice', 'headroom', 'rtt', 'loss', 'fps'];


export function AdminCalls() {
  const [settings, setSettings] = useState<CallSettings | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [summary, setSummary] = useState<AdminCallSummary | null>(null);
  const [calls, setCalls] = useState<AdminCallRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminCallDetail | null>(null);
  const [metric, setMetric] = useState<CallMetric>('bitrate');

  const load = useCallback(async () => {
    try {
      const next = await api.callSettings();
      setSettings(next);
      setDraft(draftFrom(next));
    } catch {
      setNotice('Не удалось загрузить настройки звонков');
    }
    try {
      const [rows, tiles] = await Promise.all([api.adminCalls(), api.adminCallsSummary(7)]);
      setCalls(rows.calls);
      setHasMore(rows.calls.length >= 30);
      setSummary(tiles);
    } catch {
      setNotice('Не удалось загрузить звонки');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** The next page, keyed on the oldest row already shown. */
  async function loadMore() {
    const oldest = calls.at(-1)?.startedAt;
    if (oldest === undefined) return;
    try {
      const next = await api.adminCalls(oldest);
      setCalls([...calls, ...next.calls]);
      setHasMore(next.calls.length >= 30);
    } catch {
      setNotice('Не удалось загрузить ещё');
    }
  }

  /** Opening a row fetches its readings; clicking it again closes it. */
  async function open(id: string) {
    if (openId === id) {
      setOpenId(null);
      setDetail(null);
      return;
    }
    setOpenId(id);
    setDetail(null);
    try {
      setDetail(await api.adminCall(id));
    } catch {
      setNotice('Не удалось загрузить звонок');
    }
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.saveCallSettings({
        callWidth: Number(draft.callWidth),
        callHeight: Number(draft.callHeight),
        callFps: Number(draft.callFps),
        callVideoKbps: Number(draft.callVideoKbps),
        callAudioKbps: Number(draft.callAudioKbps),
        callDegradation: draft.callDegradation,
        callCodec: draft.callCodec,
        callAudioFec: draft.callAudioFec,
        callRelayFirst: draft.callRelayFirst,
        callStatsSeconds: Number(draft.callStatsSeconds),
        callSamplesDays: Number(draft.callSamplesDays),
      });
      setSettings(next);
      setDraft(draftFrom(next));
      setNotice('Сохранено — подействует со следующего звонка');
    } catch {
      setNotice('Не удалось сохранить');
    }
    setBusy(false);
  }

  async function reset() {
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.resetCallSettings();
      setSettings(next);
      setDraft(draftFrom(next));
      setNotice('Вернули значения из .env');
    } catch {
      setNotice('Не удалось сбросить');
    }
    setBusy(false);
  }

  if (!settings || !draft) {
    return <p className="admin-note">{notice ?? 'Загружаем…'}</p>;
  }

  /** The text inputs only: the two pickers and the switches are their own shapes. */
  type TextKey = Exclude<
    keyof Draft,
    'callDegradation' | 'callCodec' | 'callAudioFec' | 'callRelayFirst'
  >;
  const field = (key: TextKey) => ({
    value: draft[key],
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      setDraft({ ...draft, [key]: event.target.value }),
  });
  const tuned = settings.overridden.length > 0;

  return (
    <>
      <section className="admin-section">
        <div className="call-head">
          <h2 className="admin-section-title">Звонки за неделю</h2>
          <button type="button" className="settings-secondary" onClick={() => void load()}>
            Обновить
          </button>
        </div>
        {summary && (
          <div className="stat-grid">
            <div className="stat">
              <div className="stat-label">Звонков</div>
              <div className="stat-value">{summary.calls}</div>
              <div className="stat-hint">{summary.answered} с ответом</div>
            </div>
            <div className="stat">
              <div className="stat-label">Через релей</div>
              <div className="stat-value">{summary.relayed}</div>
              <div className="stat-hint">остальные соединились напрямую</div>
            </div>
            <div className="stat">
              <div className="stat-label">Битрейт видео</div>
              <div className="stat-value">{bitrate(summary.medianVideoBps)}</div>
              <div className="stat-hint">медиана по звонку</div>
            </div>
            <div className="stat">
              <div className="stat-label">Задержка</div>
              <div className="stat-value">
                {summary.medianRttMs === null ? '—' : `${summary.medianRttMs} мс`}
              </div>
              <div className="stat-hint">медиана rtt</div>
            </div>
            <div className="stat">
              <div className="stat-label">В лимите</div>
              <div className="stat-value">{summary.limited}</div>
              <div className="stat-hint">кодировщику не хватало ≥20% звонка</div>
            </div>
          </div>
        )}

        <div className="call-list">
          {calls.length === 0 && (
            <p className="admin-note">
              Звонков ещё нет. Строка появляется с первого гудка, замеры — со второй секунды
              разговора.
            </p>
          )}
          {calls.map((call) => (
            <div key={call.id}>
              <button
                type="button"
                className={openId === call.id ? 'call-row call-row-open' : 'call-row'}
                onClick={() => void open(call.id)}
              >
                <span>
                  <span className="call-row-when">{formatLastSeen(call.startedAt)}</span>
                  <span className="call-row-line">
                    {call.video ? ' · видео' : ' · звук'}
                    {call.relayed === true ? ' · релей' : call.relayed === false ? ' · напрямую' : ''}
                  </span>
                </span>
                <span>
                  {call.callerName} → {call.calleeName}
                  <span className="call-row-line">
                    {' '}
                    · {shell(call.callerShell)} / {shell(call.calleeShell)}
                  </span>
                </span>
                <span className="call-row-numbers">
                  <span>{outcome(call)}</span>
                  <span>видео {bitrate(call.videoOutBpsAvg)}</span>
                  <span>rtt {call.rttMsAvg === null ? '—' : `${call.rttMsAvg} мс`}</span>
                  <span>потери {percent(call.lossAvg)}</span>
                  {call.limitedShareMax !== null && call.limitedShareMax > 0 && (
                    <span>лимит {percent(call.limitedShareMax)}</span>
                  )}
                  {call.freezesMax !== null && call.freezesMax > 0 && (
                    <span>фризов {call.freezesMax}</span>
                  )}
                  {call.notes > 0 && <span className="pill pill-warn">заметок {call.notes}</span>}
                </span>
                <span className="call-row-line">{openId === call.id ? 'свернуть' : 'открыть'}</span>
              </button>

              {openId === call.id && (
                <div className="call-detail">
                  {!detail && <p className="admin-note">Загружаем замеры…</p>}
                  {detail && (
                    <>
                      <div className="call-detail-metrics">
                        {METRICS.map((entry) => (
                          <button
                            key={entry}
                            type="button"
                            className={
                              entry === metric ? 'settings-secondary is-active' : 'settings-secondary'
                            }
                            onClick={() => setMetric(entry)}
                          >
                            {CALL_METRIC_LABEL[entry]}
                          </button>
                        ))}
                      </div>

                      {detail.sides.map((side) => (
                        <div key={side.userId}>
                          <h4 className="call-side-title">
                            {side.name} · {side.role === 'caller' ? 'звонил' : 'отвечал'} ·{' '}
                            {shell(side.shell)} · {peer(side.via)}
                          </h4>
                          <p className="admin-note">
                            {side.samples.length} замеров
                            {side.truncated ? ' (показаны первые 3000)' : ''}
                            {side.agent ? ` · ${side.agent.slice(0, 90)}` : ''}
                            {side.samples.at(-1)?.route ? ` · ${side.samples.at(-1)?.route}` : ''}
                            {side.samples.at(-1)?.codecOut
                              ? ` · кодек ${side.samples.at(-1)?.codecOut}`
                              : ''}
                          </p>
                          <AdminCallChart
                            samples={side.samples}
                            metric={metric}
                            startedAt={detail.call.startedAt}
                          />
                        </div>
                      ))}

                      <div className="call-log">
                        {detail.events.length === 0 && (
                          <p className="admin-note">Ни одна сторона не прислала событий.</p>
                        )}
                        {detail.events.map((event, index) => (
                          <div key={`${event.at}-${index}`} className="call-log-line">
                            <span>
                              {formatDuration(Math.max(0, event.at - detail.call.startedAt))}
                            </span>
                            <span className="call-log-kind">
                              {event.kind} · {event.name.split(' ')[0]}
                            </span>
                            <span>{event.detail}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {hasMore && (
          <div className="admin-actions">
            <button type="button" className="settings-secondary" onClick={() => void loadMore()}>
              Ещё
            </button>
          </div>
        )}
      </section>

      <section className="admin-section">
        <div className="admin-tuning">
          <div className="admin-tuning-head">
            <h3 className="admin-tuning-title">Качество звонка</h3>
            <span className="admin-tuning-hint">
              Эти цифры уезжают на устройства вместе с доступом к релею, а его каждый звонок
              запрашивает заново — поэтому изменение действует со следующего звонка, без
              перезагрузки страницы и без пересборки приложения.
            </span>
            {tuned && <span className="pill pill-warn">переопределено здесь, не из .env</span>}
          </div>

          <div className="admin-fields">
            <label className="admin-field">
              <span>Ширина кадра, px</span>
              <input type="number" min={160} max={1920} step={16} {...field('callWidth')} />
              <small>из .env: {settings.envDefaults.callWidth} px</small>
            </label>
            <label className="admin-field">
              <span>Высота кадра, px</span>
              <input type="number" min={120} max={1080} step={8} {...field('callHeight')} />
              <small>из .env: {settings.envDefaults.callHeight} px</small>
            </label>
            <label className="admin-field">
              <span>Кадров в секунду</span>
              <input type="number" min={8} max={30} {...field('callFps')} />
              <small>из .env: {settings.envDefaults.callFps}</small>
            </label>
            <label className="admin-field">
              <span>Битрейт видео, кбит/с</span>
              <input type="number" min={100} max={4000} step={50} {...field('callVideoKbps')} />
              <small>из .env: {settings.envDefaults.callVideoKbps} кбит/с</small>
            </label>
            <label className="admin-field">
              <span>Битрейт звука, кбит/с</span>
              <input type="number" min={16} max={128} step={8} {...field('callAudioKbps')} />
              <small>из .env: {settings.envDefaults.callAudioKbps} кбит/с</small>
            </label>
            <label className="admin-field">
              <span>Чем жертвовать</span>
              <select
                value={draft.callDegradation}
                onChange={(event) =>
                  setDraft({ ...draft, callDegradation: event.target.value as CallDegradation })
                }
              >
                {DEGRADATIONS.map((entry) => (
                  <option key={entry.value} value={entry.value}>
                    {entry.label}
                  </option>
                ))}
              </select>
              <small>
                когда сеть не тянет · разговор обычно хочет плавность · в приложении на iOS пока
                не действует
              </small>
            </label>
            <label className="admin-field">
              <span>Кодек видео</span>
              <select
                value={draft.callCodec}
                onChange={(event) =>
                  setDraft({ ...draft, callCodec: event.target.value as CallCodec })
                }
              >
                {CODECS.map((codec) => (
                  <option key={codec} value={codec}>
                    {codec === 'auto' ? 'Как решит браузер' : codec}
                  </option>
                ))}
              </select>
              <small>
                предлагается первым · которого у устройства нет — не предлагается вовсе · в
                приложении на iOS пока не действует
              </small>
            </label>
            <div className="admin-field">
              <label className="admin-switch admin-switch-inline">
                <input
                  type="checkbox"
                  checked={draft.callAudioFec}
                  onChange={(event) => setDraft({ ...draft, callAudioFec: event.target.checked })}
                />
                <span>Избыточность звука</span>
              </label>
              <small>
                Opus FEC: копия предыдущего пакета внутри следующего · тратит биты только
                когда есть потери · при 13–18 % это разница между дырой в слове и словом
              </small>
            </div>
            <div className="admin-field">
              <label className="admin-switch admin-switch-inline">
                <input
                  type="checkbox"
                  checked={draft.callRelayFirst}
                  onChange={(event) => setDraft({ ...draft, callRelayFirst: event.target.checked })}
                />
                <span>Сначала через релей</span>
              </label>
              <small>
                звонящий открывается без прямого пути, а если через релей за 6 с ничего не
                собралось — снимает запрет и дозванивается напрямую · на вечер, не насовсем · в
                журнале: «relay first», потом call/relay/held или call/relay/fallback
              </small>
            </div>
            <label className="admin-field">
              <span>Замер раз в, с</span>
              <input type="number" min={2} max={30} {...field('callStatsSeconds')} />
              <small>из .env: {settings.envDefaults.callStatsSeconds} с</small>
            </label>
            <label className="admin-field">
              <span>Хранить замеры, дней</span>
              <input type="number" min={1} max={365} {...field('callSamplesDays')} />
              <small>сами звонки хранятся всегда · из .env: {settings.callSamplesDays} дн.</small>
            </label>
            <div className="admin-field admin-field-readout">
              <span>Минута звонка</span>
              <strong>≈{settings.mbPerMinute} МБ</strong>
              <small>
                в одну сторону, видео + звук. Через релей тот же звонок стоит{' '}
                <b>≈{settings.relayMbitps} Мбит/с</b> — обе стороны считаются и на входе, и на
                выходе, поэтому поднимать битрейт надо вместе с <code>bps-capacity</code> и{' '}
                <code>max-bps</code> в <code>turnserver.conf</code>, иначе coturn начнёт ронять
                пакеты, а отправитель примет это за затор.
              </small>
            </div>
          </div>

          <div className="admin-actions">
            <button
              type="button"
              className="primary-button admin-save"
              disabled={busy}
              onClick={() => void save()}
            >
              Сохранить
            </button>
            <button
              type="button"
              className="settings-secondary"
              disabled={busy || !tuned}
              onClick={() => void reset()}
            >
              Вернуть значения из .env
            </button>
            {notice && <span className="admin-note">{notice}</span>}
          </div>
        </div>
      </section>
    </>
  );
}
